<template>
  <div class="documents-view">
    <SectionCard title="Dokument">
      <p v-if="isDocumentsLoading" class="text-subtle">Hämtar dokument...</p>
      <p v-else-if="documentsError" class="error-text">{{ documentsError }}</p>
      <template v-else>
        <AppTable v-if="documents.length">
          <thead>
            <tr>
              <th>Dokumenttyp</th>
              <th>Id</th>
              <th>Filtyp</th>
              <th>Skapat</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <AppTableRow
              v-for="document in documents"
              :key="document.id"
              class="document-row"
              @click="openDocument(document)"
            >
              <AppTableCell>{{ formatDocumentType(document.type) }}</AppTableCell>
              <AppTableCell class="document-id">{{ document.id }}</AppTableCell>
              <AppTableCell>{{ document.fileType.ext.toUpperCase() }}</AppTableCell>
              <AppTableCell nowrap>{{ formatDateTimeValue(document.createdAt) }}</AppTableCell>
              <AppTableCell action>
                <span @click.stop>
                  <AppButton
                    icon
                    small
                    :loading="openingDocumentId === document.id"
                    :aria-label="`Visa ${formatDocumentType(document.type)}`"
                    title="Visa"
                    @click="openDocument(document)"
                  >
                    <SvgIcon :path="mdiMagnify" size="18" />
                  </AppButton>
                </span>
              </AppTableCell>
            </AppTableRow>
          </tbody>
        </AppTable>
        <p v-else class="text-subtle">Inga dokument.</p>
      </template>
    </SectionCard>

    <SectionCard title="Ladda upp dokument">
      <div class="stack">
        <div class="filter-row">
          <AppSelect
            v-model="selectedType"
            label="Dokumenttyp"
            :options="documentTypeOptions"
            placeholder="Välj dokumenttyp"
          />
          <AppSelect v-model="selectedUserId" label="Låntagare" :options="debtorOptions" placeholder="Välj låntagare" />
        </div>

        <AppDocumentUpload v-model="selectedFiles" :disabled="isUploading" />

        <div class="actions">
          <AppButton color="green" :disabled="!canUpload" :loading="isUploading" @click="handleUpload">
            Ladda upp
          </AppButton>
        </div>
      </div>
    </SectionCard>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { mdiMagnify } from '@mdi/js'
import { formatDateTimeValue } from '@staff/shared'
import {
  AppButton,
  AppDocumentUpload,
  AppSelect,
  AppTable,
  AppTableCell,
  AppTableRow,
  SectionCard,
  SvgIcon,
} from '@staff/ui'

import { useMetadata } from '@/composables/useMetadata'
import { fetchDocuments, type DistributorDocument } from '@/services/application'
import { uploadDebtDocument } from '@/services/debt'
import { type Debt } from '../debt.types'
import { useDocumentPreview } from '@/composables/documentPreview'

const props = defineProps<{ debt: Debt }>()

const { translateDocumentType, getReferenceValues } = useMetadata()

const documents = ref<DistributorDocument[]>([])
const documentsError = ref('')
const isDocumentsLoading = ref(false)

const selectedType = ref('')
const selectedUserId = ref('')
const selectedFiles = ref<File[]>([])
const isUploading = ref(false)

const formatDocumentType = (type: string | null | undefined) => {
  if (!type) return '-'
  return translateDocumentType(type)
}

const { openingDocumentId, openDocument } = useDocumentPreview((document) => formatDocumentType(document.type))

const documentTypeOptions = computed(() =>
  getReferenceValues('debtDocumentUploadTypes').map((ref) => ({
    value: ref.code,
    text: ref.label,
  })),
)

const debtorOptions = computed(() =>
  props.debt.debtors.map((debtor) => ({
    value: debtor.userId,
    text: `${debtor.givenName} ${debtor.surname}`,
  })),
)

const canUpload = computed(() => selectedFiles.value.length > 0 && selectedType.value !== '' && !isUploading.value)

const loadDocuments = async () => {
  isDocumentsLoading.value = true
  documentsError.value = ''

  try {
    documents.value = await fetchDocuments({ debtId: props.debt.id })
  } catch (err) {
    documents.value = []
    documentsError.value = err instanceof Error ? err.message : 'Kunde inte hämta dokument.'
  } finally {
    isDocumentsLoading.value = false
  }
}

const handleUpload = async () => {
  if (!selectedType.value || selectedFiles.value.length === 0) return

  isUploading.value = true
  const files = [...selectedFiles.value]

  try {
    for (const file of files) {
      await uploadDebtDocument(props.debt.id, {
        file,
        type: selectedType.value,
        userId: selectedUserId.value || undefined,
      })
      selectedFiles.value = selectedFiles.value.filter((selected) => selected !== file)
    }

    selectedType.value = ''
    selectedUserId.value = ''
  } finally {
    isUploading.value = false
    await loadDocuments()
  }
}

onMounted(() => {
  const mainDebtor = props.debt.debtors.find((d) => d.roles.isMainDebtor) ?? props.debt.debtors[0]
  if (mainDebtor) {
    selectedUserId.value = mainDebtor.userId
  }

  void loadDocuments()
})
</script>

<style scoped lang="scss">
.documents-view {
  display: flex;
  flex-direction: column;
  gap: var(--space-24);
  margin-top: var(--space-24);
}

.stack {
  display: flex;
  flex-direction: column;
  gap: var(--space-16);
}

.filter-row {
  display: flex;
  flex-direction: column;
  gap: var(--space-16);
}

@media (min-width: 40rem) {
  .filter-row {
    flex-direction: row;
  }
}

.actions {
  display: flex;
  justify-content: flex-end;
}

.document-id {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: var(--font-size-xs);
  line-height: var(--font-size-xs-line-height);
}

.document-row {
  cursor: pointer;
}
</style>
